import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { Observable, of, take, tap } from 'rxjs';
import { ComponentCredentialsService } from './component-credentials.service';
import { ComponentCredentialDto } from '../models/component-credentials.model';

type ComponentCredentialsState = {
  credentials: ComponentCredentialDto[];
  selectedCredential: ComponentCredentialDto | null;
  loading: boolean;
  saving: boolean;
  error: unknown | null;
};

const initialState: ComponentCredentialsState = {
  credentials: [],
  selectedCredential: null,
  loading: false,
  saving: false,
  error: null,
};

export interface GetComponentCredentialPayload {
  componentCredentialId: string;
}

export interface UpdateComponentCredentialPayload {
  componentCredentialId: string;
  payload: ComponentCredentialDto;
}

export const ComponentCredentialsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store) => ({
    hasCredentials: computed(() => store.credentials().length > 0),
    busy: computed(() => store.loading() || store.saving()),
  })),
  withMethods(
    (
      store,
      componentCredentialsService = inject(ComponentCredentialsService),
    ) => {
      const upsertCredential = (
        credentials: ComponentCredentialDto[],
        credential: ComponentCredentialDto,
        fallbackCredentialId?: string,
      ): ComponentCredentialDto[] => {
        const resolvedCredentialId =
          credential.id ?? fallbackCredentialId ?? '';
        if (!resolvedCredentialId) {
          return [...credentials, credential];
        }

        let foundMatch = false;
        const updatedCredentials = credentials.map((existingCredential) => {
          if (existingCredential.id !== resolvedCredentialId) {
            return existingCredential;
          }
          foundMatch = true;
          return {
            ...existingCredential,
            ...credential,
            id: resolvedCredentialId,
          };
        });

        if (foundMatch) {
          return updatedCredentials;
        }

        return [
          ...updatedCredentials,
          { ...credential, id: resolvedCredentialId },
        ];
      };

      const fetchCredential$ = ({
        componentCredentialId,
      }: GetComponentCredentialPayload): Observable<ComponentCredentialDto> => {
        patchState(store, { loading: true, error: null });
        return componentCredentialsService.getById(componentCredentialId).pipe(
          take(1),
          tap({
            next: (credential) => {
              patchState(store, {
                credentials: upsertCredential(
                  store.credentials(),
                  credential,
                  componentCredentialId,
                ),
                selectedCredential: credential,
                loading: false,
              });
            },
            error: (error: unknown) => {
              patchState(store, { error, loading: false });
            },
          }),
        );
      };

      return {
        getAllComponentCredentials(): void {
          patchState(store, { loading: true, error: null });
          componentCredentialsService
            .getAll()
            .pipe(take(1))
            .subscribe({
              next: (result) => {
                patchState(store, {
                  credentials: result.clients ?? [],
                  loading: false,
                });
              },
              error: (error: unknown) => {
                patchState(store, { error, loading: false });
              },
            });
        },

        getComponentCredential(payload: GetComponentCredentialPayload): void {
          fetchCredential$(payload).subscribe();
        },

        getComponentCredentialFromStoreOrFetch({
          componentCredentialId,
        }: GetComponentCredentialPayload): Observable<ComponentCredentialDto> {
          const cachedCredential =
            store
              .credentials()
              .find((credential) => credential.id === componentCredentialId) ??
            null;
          if (cachedCredential) {
            patchState(store, {
              error: null,
              selectedCredential: cachedCredential,
            });
            return of(cachedCredential);
          }

          return fetchCredential$({ componentCredentialId });
        },

        createComponentCredential(payload: ComponentCredentialDto): void {
          patchState(store, { saving: true, error: null });
          componentCredentialsService
            .create(payload)
            .pipe(take(1))
            .subscribe({
              next: (credential) => {
                patchState(store, {
                  credentials: upsertCredential(
                    store.credentials(),
                    credential,
                    payload.id,
                  ),
                  selectedCredential: credential,
                  saving: false,
                });
              },
              error: (error: unknown) => {
                patchState(store, { error, saving: false });
              },
            });
        },

        updateComponentCredential({
          componentCredentialId,
          payload,
        }: UpdateComponentCredentialPayload): void {
          patchState(store, { saving: true, error: null });
          componentCredentialsService
            .update(componentCredentialId, payload)
            .pipe(take(1))
            .subscribe({
              next: (credential) => {
                patchState(store, {
                  credentials: upsertCredential(
                    store.credentials(),
                    credential,
                    componentCredentialId,
                  ),
                  selectedCredential: {
                    ...credential,
                    id: credential.id ?? componentCredentialId,
                  },
                  saving: false,
                });
              },
              error: (error: unknown) => {
                patchState(store, { error, saving: false });
              },
            });
        },

        deleteComponentCredential(componentCredentialId: string): void {
          patchState(store, { saving: true, error: null });
          componentCredentialsService
            .delete(componentCredentialId)
            .pipe(take(1))
            .subscribe({
              next: () => {
                patchState(store, {
                  credentials: store
                    .credentials()
                    .filter(
                      (credential) => credential.id !== componentCredentialId,
                    ),
                  selectedCredential:
                    store.selectedCredential()?.id === componentCredentialId
                      ? null
                      : store.selectedCredential(),
                  saving: false,
                });
              },
              error: (error: unknown) => {
                patchState(store, { error, saving: false });
              },
            });
        },

        clearError(): void {
          patchState(store, { error: null });
        },

        reset(): void {
          patchState(store, initialState);
        },
      };
    },
  ),
);
